/**
 * Public Wedding-Party Member mini-page at /site/:slug/party/:guestId.
 *
 * Anyone can view; nothing private leaks. Visitors can post guest-book
 * messages without an account. Couple can hide messages from their
 * dashboard later (see PATCH /party-messages/{id}/hide).
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Heart, Instagram, MessageCircle, Send, Sparkles, Share2, Camera, ImagePlus, Download } from "lucide-react";
import axios from "axios";
import { WaiverForm } from "../components/WaiverForm";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABEL = {
  bride: "The Bride", groom: "The Groom",
  maid_of_honor: "Maid of Honor", matron_of_honor: "Matron of Honor",
  best_man: "Best Man", bridesmaid: "Bridesmaid", groomsman: "Groomsman",
  flower_girl: "Flower Girl", ring_bearer: "Ring Bearer",
  parent: "Parent", officiant: "Officiant", usher: "Usher", other: "Wedding Party",
};

export default function PartyMemberPage() {
  const { slug, guestId } = useParams();
  useEffect(() => { if (guestId) localStorage.setItem(`lif_guest_${slug}`, guestId); }, [slug, guestId]);
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [needWaiver, setNeedWaiver] = useState(false);
  const [from, setFrom] = useState("");
  const [msg, setMsg] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const fileInputRef = useRef(null);

  async function load() {
    try {
      const [{ data: d }, { data: m }] = await Promise.all([
        axios.get(`${BACKEND}/api/public/site/${slug}/party/${guestId}`),
        axios.get(`${BACKEND}/api/public/site/${slug}/party/${guestId}/messages`),
      ]);
      setData(d);
      setMessages(m);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  }
  useEffect(() => { load(); }, [slug, guestId]); // eslint-disable-line react-hooks/exhaustive-deps

  function pickPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Photo must be under 10MB");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  }

  async function postMessage(e) {
    e.preventDefault();
    if (!from.trim() || !msg.trim()) return toast.error("Name and message required");
    if (photoFile && !localStorage.getItem(`lif_waiver_${slug}`)) return setNeedWaiver(true);
    setBusy(true);
    try {
      let photo_path;
      if (photoFile) {
        const form = new FormData();
        form.append("file", photoFile);
        form.append("waiver_token", localStorage.getItem(`lif_waiver_${slug}`) || "");
        const { data: up } = await axios.post(
          `${BACKEND}/api/public/site/${slug}/party/${guestId}/messages/photo`,
          form,
        );
        photo_path = up.storage_path;
      }
      await axios.post(`${BACKEND}/api/public/site/${slug}/party/${guestId}/messages`, {
        from_name: from, message: msg, ...(photo_path ? { photo_path } : {}),
      });
      toast.success(photoFile ? "Photo + note posted!" : "Posted!");
      setFrom(""); setMsg(""); setPhotoFile(null); setPhotoPreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (err) {
      if (err?.response?.status === 403 && photoFile) { localStorage.removeItem(`lif_waiver_${slug}`); setNeedWaiver(true); }
      toast.error(err?.response?.data?.detail || "Could not post");
    } finally { setBusy(false); }
  }

  async function shareCard(layout) {
    const url = `${BACKEND}/api/public/site/${slug}/party/${guestId}/share-card.png?layout=${layout}`;
    try {
      const r = await fetch(url);
      const blob = await r.blob();
      const file = new File([blob], `roster-${guestId}-${layout}.png`, { type: "image/png" });
      // Try Web Share API first (mobile)
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `${data?.member?.name} · Locked In Roster`,
            text: `Locked in forever 💍 — ${data?.wedding?.couple_names}`,
          });
          return;
        } catch { /* user cancelled — fall through to download */ }
      }
      // Fallback: trigger download
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = file.name;
      a.click();
      toast.success("Card downloaded");
    } catch {
      toast.error("Could not generate card");
    }
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg)] p-6 text-center">
        <Crown className="w-10 h-10 text-[var(--gold)] mb-4" />
        <h1 className="font-serif text-3xl mb-2">Member not found</h1>
        <p className="text-[var(--muted)] mb-6 max-w-md">This wedding-party member's mini-page isn't available, or the couple hasn't enabled their public profile yet.</p>
        <Link to={`/site/${slug}`} className="btn-gold">Back to wedding</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  const { wedding, member, outfit_teasers } = data;
  const ig = (member.instagram || "").replace(/^@/, "");

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="party-member-page">
      {needWaiver && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setNeedWaiver(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="party-waiver-modal">
            <WaiverForm drawn withEmail cta="Sign & post my photo"
              submit={async (b) => (await axios.post(`${BACKEND}/api/public/weddings/${slug}/waiver`, b)).data}
              onSigned={(d) => { localStorage.setItem(`lif_waiver_${slug}`, d.token); setNeedWaiver(false); toast.success("Thanks! Tap Post again to share your photo."); }} />
          </div>
        </div>
      )}
      {/* Hero */}
      <header className="bg-[var(--ink)] text-white py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Link to={`/site/${slug}`} className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] hover:underline">
            ← {wedding.couple_names}
          </Link>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mt-3 mb-4 flex items-center justify-center gap-1">
            <Crown className="w-3 h-3" /> Locked In Roster
          </p>
          {member.photo_url ? (
            <img src={member.photo_url} alt={member.name} data-testid="member-photo" className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-[var(--gold)] mb-6" />
          ) : (
            <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center mx-auto border-4 border-[var(--gold)] mb-6">
              <span className="font-serif text-5xl text-[var(--gold)]">{(member.name || "?")[0]}</span>
            </div>
          )}
          <h1 className="font-serif font-light text-5xl md:text-6xl mb-2" data-testid="member-name">{member.name}</h1>
          <p className="font-script text-[var(--gold)] text-3xl mb-1">{ROLE_LABEL[member.wedding_party_role] || "Wedding Party"}</p>
          <p className="text-white/60 text-sm">
            {member.relationship && <>{member.relationship}</>}
            {member.relationship && member.pronouns && " · "}
            {member.pronouns}
          </p>
          {ig && (
            <a href={`https://instagram.com/${ig}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-white/80 hover:text-[var(--gold)] text-sm" data-testid="member-instagram">
              <Instagram className="w-4 h-4" /> @{ig}
            </a>
          )}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button onClick={() => shareCard("square")} data-testid="share-card-square" className="btn-gold text-xs">
              <Share2 className="w-3 h-3" /> Square card
            </button>
            <button onClick={() => shareCard("story")} data-testid="share-card-story" className="btn-outline-gold text-xs !text-white !border-white/30">
              <Download className="w-3 h-3" /> Story card
            </button>
          </div>
        </div>
      </header>

      {/* Bio */}
      {member.bio && (
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Sparkles className="w-5 h-5 text-[var(--gold)] mx-auto mb-4" />
          <p className="font-serif text-2xl md:text-3xl leading-relaxed text-[var(--ink)]" data-testid="member-bio">{member.bio}</p>
        </section>
      )}

      {/* Outfit teasers */}
      {outfit_teasers?.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 py-12">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] text-center mb-6">The look</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {outfit_teasers.map((o, i) => (
              <div key={i} className="bg-white border border-[var(--line)] rounded-2xl p-5 text-center" data-testid={`outfit-teaser-${i}`}>
                <p className="font-serif text-xl capitalize">{o.garment_type}</p>
                {o.color && <p className="text-sm text-[var(--muted)] mt-1">{o.color}</p>}
                {o.vendor && <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)] mt-2">{o.vendor}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Browse all photos link */}
      <section className="max-w-4xl mx-auto px-6 py-2 text-center">
        <Link
          to={`/site/${slug}/party/${guestId}/photos`}
          data-testid="browse-gallery-link"
          className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] hover:underline"
        >
          Browse all guest photos →
        </Link>
      </section>

      {/* Guest book */}
      <section className="max-w-2xl mx-auto px-6 py-16" data-testid="guestbook">
        <div className="text-center mb-8">
          <Heart className="w-6 h-6 text-[var(--gold)] mx-auto mb-3" />
          <h2 className="font-serif text-3xl mb-2">Leave them a note</h2>
          <p className="text-[var(--muted)] text-sm">Cheer on {member.name?.split(" ")[0]} ahead of the big day. Couples can hide messages they'd rather not show.</p>
        </div>

        <form onSubmit={postMessage} className="bg-white border border-[var(--line)] rounded-2xl p-6 mb-8 space-y-3">
          <input
            value={from}
            onChange={(e) => setFrom(e.target.value.slice(0, 80))}
            placeholder="Your name"
            data-testid="gb-from"
            className="input-luxe"
          />
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value.slice(0, 500))}
            placeholder={`Write something nice for ${member.name?.split(" ")[0]}…`}
            rows={3}
            data-testid="gb-message"
            className="input-luxe resize-none"
          />

          {/* Optional photo of the visitor with the WP member */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={pickPhoto}
              className="hidden"
              data-testid="gb-photo-input"
            />
            {photoPreview ? (
              <div className="relative inline-block" data-testid="gb-photo-preview">
                <img src={photoPreview} alt="preview" className="rounded-xl max-h-40 border border-[var(--line)]" />
                <button
                  type="button"
                  onClick={() => { setPhotoFile(null); setPhotoPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  data-testid="gb-photo-remove"
                  className="absolute top-1 right-1 bg-white/95 rounded-full p-1 shadow"
                >
                  ×
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="gb-photo-add"
                className="text-xs text-[var(--gold)] hover:underline flex items-center gap-1"
              >
                <ImagePlus className="w-3 h-3" /> Add a photo of you together (optional)
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-[10px] text-[var(--muted)]">{msg.length}/500</p>
            <button type="submit" disabled={busy} data-testid="gb-submit" className="btn-gold text-xs">
              <Send className="w-3 h-3" /> {busy ? "Posting…" : "Post"}
            </button>
          </div>
        </form>

        <div className="space-y-3">
          {messages.map((m) => (
            <div key={m.message_id} className="bg-white border border-[var(--line)] rounded-2xl p-5" data-testid={`gb-msg-${m.message_id}`}>
              {m.photo_url && (
                <img
                  src={`${BACKEND}${m.photo_url}`}
                  alt={`${m.from_name} with ${member.name}`}
                  className="rounded-xl mb-3 max-h-72 w-full object-cover"
                  data-testid={`gb-msg-photo-${m.message_id}`}
                />
              )}
              <p className="text-[var(--ink)] mb-2">{m.message}</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--gold)]">— {m.from_name}</p>
            </div>
          ))}
          {messages.length === 0 && (
            <p className="text-[var(--muted)] text-sm text-center py-6">
              <MessageCircle className="w-4 h-4 mx-auto mb-2 opacity-50" />
              Be the first to leave a note.
            </p>
          )}
        </div>
      </section>

      {/* Footer back to wedding */}
      <footer className="border-t border-[var(--line)] py-10 text-center bg-white">
        <p className="font-script text-[var(--gold)] text-3xl mb-2">{wedding.couple_names}</p>
        {wedding.wedding_date && (
          <p className="text-xs uppercase tracking-[0.22em] text-[var(--muted)] mb-4">
            {(wedding.wedding_date || "").slice(0, 10)} · {wedding.city || wedding.venue}
          </p>
        )}
        <Link to={`/site/${slug}`} className="btn-outline-gold text-xs">Visit the wedding site</Link>
      </footer>
    </div>
  );
}
